/**
 * 管理サイト(PC)の Web Push 購読。
 * 船員アプリの services/push.ts と同じ流れ（許可→購読→ /api/push/subscribe へ登録）。
 * SWは public/push-sw.js（通知の受信・クリックのみ。画面の通信には関与しない）。
 */
import { get, post } from './api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function swRegistration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register('/push-sw.js');
  return navigator.serviceWorker.ready;
}

export async function isPushEnabled(): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return !!sub && Notification.permission === 'granted';
  } catch { return false; }
}

/** 通知を有効化（許可要求→購読→サーバー登録） */
export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!pushSupported()) return { ok: false, reason: 'このブラウザは通知に対応していません' };
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, reason: '通知が許可されませんでした（ブラウザの設定から許可してください）' };
    const reg = await swRegistration();
    const res = await get<{ data: { key: string; enabled: boolean } }>('/push/vapid-public-key');
    if (!res.data?.enabled || !res.data.key) return { ok: false, reason: 'サーバー側で通知が無効です' };
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(res.data.key) as BufferSource });
    }
    const json = sub.toJSON();
    await post('/push/subscribe', { endpoint: sub.endpoint, keys: json.keys });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : '通知の有効化に失敗しました' };
  }
}
